"use client";

import { useCallback, useEffect, useRef, useState } from "react";
import { BrandMark } from "./BrandMark";
import { CTAArrow } from "./CTAArrow";

const NAV_LINKS = [
  { href: "/case-studies", label: "Work" },
  { href: "/services", label: "Capabilities" },
  { href: "/about", label: "About" },
] as const;

export function SiteHeader() {
  const [navTheme, setNavTheme] = useState<"dark" | "light">("dark");
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const drawerRef = useRef<HTMLDivElement>(null);
  const menuButtonRef = useRef<HTMLButtonElement>(null);
  const navFrameRef = useRef<number | null>(null);

  const closeMobileNav = useCallback(() => {
    setMobileNavOpen(false);
  }, []);

  const updateNavTheme = useCallback(() => {
    const sampleY = 36;
    const sections = document.querySelectorAll<HTMLElement>("[data-nav-theme]");

    for (const section of sections) {
      const rect = section.getBoundingClientRect();

      if (rect.top <= sampleY && rect.bottom > sampleY) {
        setNavTheme(section.dataset.navTheme === "light" ? "light" : "dark");
        return;
      }
    }
  }, []);

  const scheduleNavThemeUpdate = useCallback(() => {
    if (navFrameRef.current !== null) {
      return;
    }

    navFrameRef.current = window.requestAnimationFrame(() => {
      navFrameRef.current = null;
      updateNavTheme();
    });
  }, [updateNavTheme]);

  useEffect(() => {
    updateNavTheme();
    window.addEventListener("scroll", scheduleNavThemeUpdate, { passive: true });
    window.addEventListener("resize", scheduleNavThemeUpdate);

    return () => {
      window.removeEventListener("scroll", scheduleNavThemeUpdate);
      window.removeEventListener("resize", scheduleNavThemeUpdate);

      if (navFrameRef.current !== null) {
        window.cancelAnimationFrame(navFrameRef.current);
        navFrameRef.current = null;
      }
    };
  }, [scheduleNavThemeUpdate, updateNavTheme]);

  useEffect(() => {
    if (!mobileNavOpen) {
      return;
    }

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        event.preventDefault();
        closeMobileNav();
        menuButtonRef.current?.focus();
      }
    };

    document.addEventListener("keydown", onKeyDown);
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    const firstLink = drawerRef.current?.querySelector<HTMLAnchorElement>("a[href]");
    firstLink?.focus();

    return () => {
      document.removeEventListener("keydown", onKeyDown);
      document.body.style.overflow = previousOverflow;
    };
  }, [closeMobileNav, mobileNavOpen]);

  return (
    <>
      <header
        className={`site-header nav-${navTheme}${mobileNavOpen ? " nav-drawer-open" : ""}`}
      >
        <a className="brand" href="/#top" aria-label="Blink X home">
          <BrandMark />
          <span>BLINK X</span>
        </a>
        <nav className="nav nav-desktop" aria-label="Primary navigation">
          {NAV_LINKS.map((item) => (
            <a key={item.href} href={item.href}>
              {item.label}
            </a>
          ))}
        </nav>
        <div className="header-end">
          <button
            ref={menuButtonRef}
            type="button"
            className="header-menu-btn"
            aria-expanded={mobileNavOpen}
            aria-controls="site-nav-drawer"
            aria-label={mobileNavOpen ? "Close menu" : "Open menu"}
            onClick={() => setMobileNavOpen((open) => !open)}
          >
            <span className="header-menu-btn-lines" aria-hidden="true">
              <span />
              <span />
              <span />
            </span>
          </button>
          <a className="header-cta" href="/contact">
            Start a project
            <CTAArrow />
          </a>
        </div>
      </header>

      <div
        className={`nav-drawer-backdrop${mobileNavOpen ? " is-open" : ""}`}
        aria-hidden={!mobileNavOpen}
        onClick={closeMobileNav}
      />

      <div
        ref={drawerRef}
        id="site-nav-drawer"
        className={`nav-drawer${mobileNavOpen ? " is-open" : ""}`}
        role="dialog"
        aria-modal="true"
        aria-label="Site navigation"
        aria-hidden={!mobileNavOpen}
        inert={!mobileNavOpen}
      >
        <nav className="nav-drawer-nav" aria-label="Sections">
          {NAV_LINKS.map((item) => (
            <a key={item.href} href={item.href} onClick={closeMobileNav}>
              {item.label}
            </a>
          ))}
        </nav>
          <a
          className="button button-light nav-drawer-cta"
          href="/contact"
          onClick={closeMobileNav}
        >
          Start a project
          <CTAArrow />
        </a>
      </div>
    </>
  );
}
